import axios, { type AxiosRequestConfig } from 'axios';
import { escape as _escape, isNil as _isNil, omitBy as _omitBy, unescape as _unescape } from 'lodash';
import moment from 'moment';

import type {
  Invitation,
  InvitationExportCsvParams,
  InvitationUpdateProps,
  InvitationWithFormResponses,
} from '@sine/sine-core-api-types';

import HttpUploadError from '../../helpers/HttpUploadError';
import serializeQueryParams from '../../helpers/serializeQueryParams';
import { uploadFile } from '../../helpers/upload';
import { type CreateInvitationPayload } from '../../pages/Invitations/Invitations.types';
import { SineHeader } from '../../shared/types';
import rxEventHub, { RxEvent } from '../events/rx';
import type { InvitationSearchCountParams, InvitationSearchParams } from './invitation.types';

function sanitizeInvitation(invitation: Invitation | InvitationUpdateProps | CreateInvitationPayload) {
  if (invitation.adminNotes) {
    invitation.adminNotes = _escape(invitation.adminNotes);
  }

  if (invitation.visitorNotes) {
    invitation.visitorNotes = _escape(invitation.visitorNotes);
  }

  return invitation;
}

function unescapeInvitation(invitation: Invitation) {
  if (invitation.adminNotes) {
    invitation.adminNotes = _unescape(invitation.adminNotes);
  }

  if (invitation.visitorNotes) {
    invitation.visitorNotes = _unescape(invitation.visitorNotes);
  }

  return invitation;
}

export default {
  async getInvitations({
    updatedAfter,
    actioned,
  }: {
    updatedAfter?: Date;
    actioned?: boolean;
  }): Promise<Array<Invitation>> {
    const params: { updatedAfter?: number; actioned?: boolean } = {};

    if (updatedAfter) {
      params.updatedAfter = moment(updatedAfter).unix();
    }

    if (actioned !== undefined) {
      params.actioned = actioned;
    }

    const resp = await axios.get(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation?${serializeQueryParams(params)}`,
    );

    return resp.data;
  },

  async getInvitation(
    invitationId: string,
    { withPreRegistrationInfo, withCreator } = { withPreRegistrationInfo: false, withCreator: false },
  ) {
    const res = await axios.get<Invitation | InvitationWithFormResponses>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/${invitationId}`,
      {
        params: { withPreRegistrationInfo, withCreator },
      },
    );

    return res.data;
  },

  async search(
    { filters, limit, offset, sort }: InvitationSearchParams,
    cancelToken: AxiosRequestConfig['cancelToken'] | undefined,
  ): Promise<{ meta: { returnedResults: number; totalResults: number }; invitations: Array<Invitation> }> {
    const {
      data: { meta, data },
    } = await axios.post(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/search?${serializeQueryParams({
        limit,
        offset,
        sort,
      })}`,
      { filters },
      { cancelToken },
    );

    const invitations: Array<Invitation> = data.map(unescapeInvitation);

    return { meta, invitations };
  },

  async getSearchResultsCount(params: InvitationSearchCountParams | Record<string, never> = {}) {
    const resp = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/search/results-count`, params);

    // This endpoint has an inconsistent return shape: a status code for success but no results,
    // and no status code but an object if there are results. This if-statement ensures this
    // function's signature is consistent and always returns an array.
    if (Object.prototype.hasOwnProperty.call(resp.data, 'resultsCount')) {
      return [];
    }

    return resp.data;
  },

  async createSingleInvitation(invitation: CreateInvitationPayload, sendEmail = true) {
    const query = !sendEmail ? '?suppressEmail=true' : '';

    const { data } = await axios.post<Invitation>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation${query}`,
      sanitizeInvitation(invitation),
    );

    rxEventHub.broadcast(RxEvent.INVITATION_UPDATED, { invitation: data });

    return unescapeInvitation(data);
  },

  async createMultipleInvitations(invitations: Array<CreateInvitationPayload>, sendEmails = true) {
    const query = !sendEmails ? '?suppressEmail=true' : '';

    const sanitizedInvitations = invitations.map((invitation) => sanitizeInvitation(invitation));

    const { data } = await axios.post<Array<Invitation & { status?: string; reason?: string }>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation${query}`,
      sanitizedInvitations,
    );

    data.forEach((invitation) => {
      rxEventHub.broadcast(RxEvent.INVITATION_UPDATED, { invitation });
    });

    return data;
  },

  async updateInvitation(invitation: Invitation, sendEmail = false) {
    const query = !sendEmail ? '?suppressEmail=true' : '';

    const { data } = await axios.post<Invitation>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/${invitation.id}${query}`,
      sanitizeInvitation(invitation),
    );

    rxEventHub.broadcast(RxEvent.INVITATION_UPDATED, { invitation: data });

    return unescapeInvitation(data);
  },

  async cancelMultipleInvitations(
    invitations: Array<{
      id: string;
      cancelled: boolean;
    }>,
  ) {
    const body = invitations.map((invitation) => ({
      id: invitation.id,
      cancelled: true,
    }));

    const { data } = await axios.patch<Array<Invitation>>(`${window.env.REACT_APP_SINE_CORE_API_URI}/invitation`, body);

    return data;
  },

  async printRemote(invitationId: string) {
    const resp = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/${invitationId}/print`);

    return resp.data;
  },

  async uploadCSV(file: File, timezone: string, sendEmails: boolean, onProgress = () => {}) {
    const resp = await uploadFile(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/csv-upload`,
      file,
      { data: JSON.stringify(_omitBy({ sendEmails, timezone }, _isNil)) },
      onProgress,
      file.name,
    );

    if (resp.status === 400) {
      throw new HttpUploadError(resp);
    }

    // CHECK THIS
    return resp.data;
  },

  async exportCSV(params: InvitationExportCsvParams) {
    await axios.get(`${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/export/csv?${serializeQueryParams(params)}`);
  },

  /**
   * Export an Apple wallet pass for invitation.
   */
  async exportAppleWalletPass(invitationId: string, teamId?: string) {
    const response = await axios.get<ArrayBuffer>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/${invitationId}/export/apple`,
      {
        responseType: 'arraybuffer',
        ...(teamId && { headers: { [SineHeader.X_SINE_TEAM_ID]: teamId } }),
      },
    );

    const dataArray = new Uint8Array(response.data);
    const passFileBlob = new Blob([dataArray], { type: 'application/vnd.apple.pkpass' });

    return passFileBlob;
  },

  /**
   * Export a Google wallet pass for invitation.
   */
  async exportGoogleWalletPass(invitationId: string, teamId?: string) {
    const response = await axios.get<string>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/${invitationId}/export/google`,
      { ...(teamId && { headers: { [SineHeader.X_SINE_TEAM_ID]: teamId } }) },
    );

    return response.data;
  },

  /**
   * Keyset paginated fetch of invitations.
   */
  async fetchPaginatedInvitations(
    keysetUri: string | undefined,
    siteIds: Array<string>,
    searchTerm: string | null,
    limit: number,
    minDate?: number,
    maxDate?: number,
  ) {
    // TODO: bump types and properly type this object.
    const filters: any = {
      cancelled: false,
      limit,
      siteIds,
    };

    if (searchTerm !== null && searchTerm.length > 2) {
      filters.searchTerm = searchTerm;
    }

    if (minDate) filters.minDate = minDate;

    if (maxDate) filters.maxDate = maxDate;

    const response = await axios.post(keysetUri ? keysetUri : `${window.env.REACT_APP_SINE_CORE_API_URI}/invitations`, {
      filters,
    });

    return response.data;
  },

  async fetchUpComingInvitations({
    siteIds,
    searchTerm,
    limit,
  }: {
    siteIds: Array<string>;
    searchTerm?: string;
    limit?: number;
  }) {
    const response = await axios.post<Array<Invitation>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/invitation/upcoming`,
      {
        siteIds,
        searchTerm,
        limit,
      },
    );

    return response.data;
  },
};
